/**
 * WHAT THE SEATS HAVE TAUGHT THEMSELVES, for a person to check: the notes the
 * room's agents read at the start of every turn (backend `boardroom/notes.py`).
 *
 * Tool notes are about how a tool behaves and carry into every room of the
 * owner's; room notes are about this room's market and stay here. Each shows
 * when it held, the lookups it rests on, and who wrote it. A wrong one can be
 * dropped, with a reason; it is kept, and no seat reads it again.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import type { AgentNote } from '../../types';
import { shortDate } from './deliverables/provenance';

/** "competitor_miner" → "Competitor miner". */
export function seatKind(archetype: string): string {
  const words = archetype.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The notes in one list, by kind of seat, each kind's in the order given. */
export function bySeat(notes: AgentNote[]): [string, AgentNote[]][] {
  const out = new Map<string, AgentNote[]>();
  for (const n of notes) out.set(n.archetype, [...(out.get(n.archetype) ?? []), n]);
  return Array.from(out.entries()).sort(([a], [b]) => a.localeCompare(b));
}

function NoteRow({
  note,
  roomId,
  onOpenItem,
  onDrop,
}: {
  note: AgentNote;
  roomId: number;
  onOpenItem: (itemId: number) => void;
  onDrop: (note: AgentNote, reason: string) => Promise<void>;
}) {
  const [dropping, setDropping] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const dropped = !!note.dropped_at;
  const who = [
    note.written_by,
    note.written_in.name && note.written_in.id !== roomId ? `in ${note.written_in.name}` : null,
    shortDate(note.updated_at ?? note.created_at),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li
      className={`rounded-md border px-2.5 py-2 ${
        dropped ? 'border-dashed border-gray-200 opacity-60 dark:border-gray-700' : 'border-gray-200 dark:border-gray-700'
      }`}
      data-testid={`note-${note.id}`}
    >
      <div className="text-sm text-gray-900 dark:text-gray-100">
        {note.tool && <span className="mr-1 font-mono text-xs text-indigo-700 dark:text-indigo-300">{note.tool}</span>}
        {note.text}
      </div>
      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Held when: {note.applies_when}</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {note.evidence.map(e =>
          e.room_id === roomId ? (
            <button
              key={e.id}
              type="button"
              onClick={() => onOpenItem(e.id)}
              className="max-w-full truncate rounded bg-gray-100 px-1.5 py-0.5 text-left text-[11px] text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              title={e.title}
            >
              #{e.id} {e.title}
            </button>
          ) : (
            <span
              key={e.id}
              className="max-w-full truncate rounded bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              title={e.room ? `${e.title} (in ${e.room})` : e.title}
            >
              #{e.id} {e.title || 'a lookup'}
              {e.room ? ` · ${e.room}` : ''}
            </span>
          ),
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-gray-400">
        {who && <span>{who}</span>}
        {dropped && <span>Dropped{note.dropped_because ? `: ${note.dropped_because}` : ''}</span>}
      </div>
      {!dropped &&
        (dropping ? (
          <form
            className="mt-1.5 flex items-center gap-1.5"
            onSubmit={async ev => {
              ev.preventDefault();
              setBusy(true);
              try {
                await onDrop(note, reason.trim());
              } finally {
                setBusy(false);
              }
            }}
          >
            <input
              autoFocus
              value={reason}
              onChange={ev => setReason(ev.target.value)}
              onKeyDown={ev => ev.key === 'Escape' && setDropping(false)}
              maxLength={250}
              placeholder="Why it is wrong (the seats won't see this)"
              className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
            <button
              type="submit"
              disabled={busy}
              className="rounded bg-red-600 px-1.5 py-0.5 text-xs text-white hover:bg-red-700 disabled:opacity-50"
            >
              Drop
            </button>
            <button
              type="button"
              onClick={() => setDropping(false)}
              className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => {
              setReason('');
              setDropping(true);
            }}
            className="mt-1 rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Drop…
          </button>
        ))}
    </li>
  );
}

function Section({
  title,
  hint,
  notes,
  roomId,
  onOpenItem,
  onDrop,
}: {
  title: string;
  hint: string;
  notes: AgentNote[];
  roomId: number;
  onOpenItem: (itemId: number) => void;
  onDrop: (note: AgentNote, reason: string) => Promise<void>;
}) {
  return (
    <section>
      <h3 className="mb-0.5 text-[11px] uppercase tracking-wide text-gray-400">{title}</h3>
      <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      {notes.length === 0 ? (
        <p className="text-xs text-gray-400">None yet.</p>
      ) : (
        <div className="space-y-3">
          {bySeat(notes).map(([archetype, ns]) => (
            <div key={archetype}>
              <div className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">{seatKind(archetype)}</div>
              <ul className="space-y-1.5">
                {ns.map(n => (
                  <NoteRow key={n.id} note={n} roomId={roomId} onOpenItem={onOpenItem} onDrop={onDrop} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function NotesPanel({
  boardroomId,
  onOpenItem,
}: {
  boardroomId: number;
  onOpenItem: (itemId: number) => void;
}) {
  const [notes, setNotes] = useState<AgentNote[] | null>(null);
  const [showDropped, setShowDropped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setNotes((await api.listBoardroomNotes(boardroomId, showDropped)).notes);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the notes');
    }
  }, [boardroomId, showDropped]);
  useEffect(() => {
    void load();
  }, [load]);

  const drop = async (note: AgentNote, reason: string) => {
    try {
      await api.dropBoardroomNote(boardroomId, note.id, reason);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not drop that note');
    }
  };

  const all = notes ?? [];
  return (
    <div className="space-y-4 p-3 text-sm" data-testid="notes-panel">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        What the seats have written for the seats after them. Every turn, a seat reads these next to
        the numbers on how its searches came back, as lessons rather than rules.
      </p>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      {notes === null ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : (
        <>
          <Section
            title="How the tools behave"
            hint="Carried into all your rooms."
            notes={all.filter(n => n.about === 'tool')}
            roomId={boardroomId}
            onOpenItem={onOpenItem}
            onDrop={drop}
          />
          <Section
            title="About this room's market"
            hint="Read only in this room."
            notes={all.filter(n => n.about === 'room')}
            roomId={boardroomId}
            onOpenItem={onOpenItem}
            onDrop={drop}
          />
        </>
      )}
      <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <input type="checkbox" checked={showDropped} onChange={e => setShowDropped(e.target.checked)} />
        Show dropped notes
      </label>
    </div>
  );
}
